import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Skeleton loader for the quote builder inline component.
 * Mimics the structure of the actual form with fields and a table.
 *
 * Keep this tree free of Radix primitives (Tabs, Select, etc.). Those compose a
 * useState setter into their trigger ref, so swapping the skeleton for the ready
 * tree re-fires that setter and can trip React's update-depth limit (#185).
 */
export function QuoteBuilderSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-48" /> {/* Title */}
          <Skeleton className="h-9 w-24" /> {/* Action button */}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex w-full gap-2">
          <Skeleton className="h-9 flex-1" />
          <Skeleton className="h-9 flex-1" />
          <Skeleton className="h-9 flex-1" />
        </div>

        <div className="space-y-2">
          <Skeleton className="h-4 w-12" /> {/* Label */}
          <Skeleton className="h-10 w-full" /> {/* Input */}
        </div>

        <div className="space-y-2">
          <Skeleton className="h-4 w-24" /> {/* Label */}
          <div className="flex items-center gap-1 rounded-t-md border bg-muted/50 p-1">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <div className="mx-1 h-6 w-px bg-border" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <div className="mx-1 h-6 w-px bg-border" />
            <Skeleton className="h-8 w-8" />
          </div>
          <div className="min-h-[100px] rounded-b-md border border-t-0 bg-muted/30 p-4">
            <Skeleton className="mb-2 h-4 w-full" />
            <Skeleton className="mb-2 h-4 w-4/5" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>

        <div className="space-y-2">
          <Skeleton className="h-4 w-20" /> {/* Label */}
          <Skeleton className="h-10 w-full" /> {/* Date picker */}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" /> {/* Label */}
            <Skeleton className="h-10 w-full" /> {/* Select */}
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" /> {/* Label */}
            <Skeleton className="h-10 w-full" /> {/* Select */}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" /> {/* Checkbox */}
            <Skeleton className="h-4 w-32" /> {/* Label */}
          </div>
          <div className="grid grid-cols-2 gap-4 pl-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
